import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { schemaToTree, type SchemaNodeInfo } from './logic';

export function SchemaTree({ schema, emptyLabel }: { schema: unknown; emptyLabel?: string }) {
  const nodes = schemaToTree(schema);
  if (nodes.length === 0) return <p className="text-xs text-muted-foreground">{emptyLabel ?? '—'}</p>;
  return (
    <ul className="flex flex-col gap-0.5 text-xs" role="tree">
      {nodes.map((n) => (
        <SchemaNode key={n.path} node={n} />
      ))}
    </ul>
  );
}

function SchemaNode({ node }: { node: SchemaNodeInfo }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const copy = () => {
    void navigator.clipboard?.writeText(node.path).then(() => toast.success(t('admin.tools.pathCopied', { path: node.path })));
  };
  return (
    <li role="treeitem" aria-expanded={node.children.length > 0 ? open : undefined}>
      <div className="group flex items-baseline gap-1.5">
        {node.children.length > 0 ? (
          <button type="button" className="w-3 text-muted-foreground" onClick={() => setOpen((o) => !o)} aria-label={open ? 'collapse' : 'expand'}>
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="w-3" />
        )}
        <span className="font-mono font-medium">{node.name}</span>
        {node.required && <span className="text-destructive">*</span>}
        <span className="font-mono text-muted-foreground">{node.type}</span>
        {node.description && <span className="truncate text-muted-foreground">— {node.description}</span>}
        <button type="button" onClick={copy} className="invisible text-muted-foreground group-hover:visible" aria-label={t('admin.tools.copyPath')}>
          <Copy className="size-3" />
        </button>
      </div>
      {open && node.children.length > 0 && (
        <ul className="ml-4 border-l pl-2" role="group">
          {node.children.map((c) => (
            <SchemaNode key={c.path} node={c} />
          ))}
        </ul>
      )}
    </li>
  );
}
